import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import {
  CampaignOutlined,
  VpnKeyOutlined,
  FingerprintOutlined,
  ExpandMoreOutlined,
  HelpOutlineOutlined,
} from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import SettingsCard from "../Settings/SettingsCard";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  group: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: theme.spacing(2),
  },
  helper: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  accordion: {
    "&:before": { display: "none" },
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  accordionSummary: {
    fontSize: "0.85rem",
    fontWeight: 600,
  },
  steps: {
    margin: 0,
    paddingLeft: theme.spacing(2.5),
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    "& li": {
      marginBottom: theme.spacing(0.75),
    },
  },
  stepsTitle: {
    fontSize: "0.82rem",
    fontWeight: 600,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
}));

const SETTING_KEYS = {
  token: "metaAdsToken",
  adAccountId: "metaAdAccountId",
};

const iconAdornment = (Icon) => ({
  startAdornment: (
    <InputAdornment position="start">
      <Icon fontSize="small" color="disabled" />
    </InputAdornment>
  ),
});

const MetaAdsAccountSettings = () => {
  const { classes } = useStyles();

  const [token, setToken] = useState("");
  const [adAccountId, setAdAccountId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/settings");
        const list = Array.isArray(data) ? data : [];
        setToken(list.find((s) => s.key === SETTING_KEYS.token)?.value || "");
        setAdAccountId(list.find((s) => s.key === SETTING_KEYS.adAccountId)?.value || "");
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);

    try {
      await api.put(`/settings/${SETTING_KEYS.token}`, { value: token.trim() });
      await api.put(`/settings/${SETTING_KEYS.adAccountId}`, { value: adAccountId.trim() });
      toast.success("Configuración de Anuncios (Meta) guardada");
    } catch (err) {
      toastError(err);
    }

    setSaving(false);
  };

  return (
    <SettingsCard
      icon={CampaignOutlined}
      title="Anuncios (Meta)"
      subtitle="Cuenta publicitaria usada por el reporte de Anuncios: resolver el nombre de campaña de un anuncio y traer los que todavía no generaron ningún ticket."
    >
    <Box className={classes.root}>
      <Accordion className={classes.accordion} elevation={0} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
          <Box display="flex" alignItems="center" gap={1}>
            <HelpOutlineOutlined fontSize="small" color="action" />
            <Typography className={classes.accordionSummary}>
              ¿Cómo consigo estos dos datos?
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography className={classes.stepsTitle}>
            ID de la cuenta publicitaria
          </Typography>
          <ol className={classes.steps}>
            <li>
              Entrá a{" "}
              <Link href="https://adsmanager.facebook.com" target="_blank" rel="noreferrer">
                adsmanager.facebook.com
              </Link>{" "}
              con la cuenta que administra los anuncios.
            </li>
            <li>
              Arriba a la izquierda, junto al nombre de la cuenta, aparece el
              ID. Si lo ves sin el prefijo (solo números), agregale{" "}
              <code>act_</code> adelante.
            </li>
          </ol>

          <Typography className={classes.stepsTitle}>
            Token con permiso ads_read
          </Typography>
          <ol className={classes.steps}>
            <li>
              Entrá a{" "}
              <Link href="https://business.facebook.com/settings" target="_blank" rel="noreferrer">
                business.facebook.com/settings
              </Link>{" "}
              (Configuración del negocio).
            </li>
            <li>
              En el menú izquierdo, <b>Usuarios → Usuarios del sistema</b>.
              Creá uno nuevo (o usá uno existente) con rol <b>Administrador</b>.
            </li>
            <li>
              Con ese usuario del sistema seleccionado, andá a{" "}
              <b>Asignar activos → Cuentas publicitarias</b>, elegí tu cuenta
              publicitaria y dale acceso de <b>Control total</b>.
            </li>
            <li>
              Botón <b>Generar nuevo token</b>: elegí la app conectada,
              marcá el permiso <b>ads_read</b> y generá el token.
            </li>
            <li>
              Copialo ahí mismo — Meta no lo vuelve a mostrar completo
              después. Pegalo en el campo de abajo.
            </li>
          </ol>

          <Typography className={classes.helper} sx={{ mt: 1 }}>
            El token de una conexión de WhatsApp/Messenger normal NO sirve
            para esto: ese solo tiene permiso para enviar mensajes, no para
            leer anuncios.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <div className={classes.group}>
        <TextField
          label="Token con permiso ads_read"
          size="small"
          variant="outlined"
          fullWidth
          type="password"
          placeholder="EAAG..."
          helperText="Token de un System User del Business Manager con acceso a la cuenta publicitaria (no el de una conexión de mensajería: ese no sirve)."
          value={token}
          onChange={(e) => setToken(e.target.value)}
          InputProps={iconAdornment(VpnKeyOutlined)}
        />

        <TextField
          label="ID de la cuenta publicitaria"
          size="small"
          variant="outlined"
          fullWidth
          placeholder="act_123456789012345"
          helperText="Meta Ads Manager, junto al nombre de la cuenta, o Configuración del negocio → Cuentas publicitarias."
          value={adAccountId}
          onChange={(e) => setAdAccountId(e.target.value)}
          InputProps={iconAdornment(FingerprintOutlined)}
        />
      </div>

      <Button
        variant="contained"
        color="primary"
        style={{ alignSelf: "flex-start" }}
        disabled={loading || saving}
        onClick={handleSave}
      >
        Guardar
      </Button>
    </Box>
    </SettingsCard>
  );
};

export default MetaAdsAccountSettings;
